import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { PageBuyingGuide } from 'common/interfaces/agg-page';
import { MuiFileUploader } from 'common/ui/file-uploader/MuiFileUploader';
import { RichTextEditorV2 } from 'common/ui/rich-text-editor/rich-text-editor-v2';
import { FC, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import type { CommonPageForm } from '../../../page-builder-v2.types';
import { styles } from './buying-guide-section.styles';

export const BuyingGuideSection: FC = () => {
  const { control, getValues } = useFormContext<CommonPageForm>();
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'buyingGuide',
  });

  const activeIndex = fields.findIndex((f) => f.type !== 'deleted');
  const activeField = activeIndex !== -1 ? fields[activeIndex] : null;
  const hasText = !!(activeField?.file_data?.text);

  const handleAdd = (file: File, preview: string) => {
    const newGuide: PageBuyingGuide = {
      assets_type: 'buyingGuide',
      file_data: { text: '' },
      type: 'new',
      file,
      preview,
    };
    append(newGuide);
  };

  const handleDelete = () => {
    if (!activeField) return;
    if (activeField.type === 'new') {
      remove(activeIndex);
    } else {
      update(activeIndex, { ...activeField, type: 'deleted' });
    }
  };

  const markAsRenew = () => {
    if (!activeField || activeField.type === 'new' || activeField.type === 'renew') return;
    const currentItem = getValues(`buyingGuide.${activeIndex}` as any);
    update(activeIndex, { ...currentItem, type: 'renew' });
  };

  const handleDialogClose = () => {
    markAsRenew();
    setTextDialogOpen(false);
  };

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { margin: '12px 0' },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
          '&.Mui-expanded': { minHeight: 52 },
          minHeight: 52,
          px: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            mr: 1,
          }}
        >
          <Typography fontWeight={600} variant="subtitle1">Buying Guide</Typography>
          {hasText && activeField && (
            <Button
              onClick={(e) => { e.stopPropagation(); setTextDialogOpen(true); }}
              size="small"
              startIcon={<EditOutlinedIcon />}
              variant="outlined"
            >
              Edit Text
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', p: 0, overflow: 'hidden' }}>
      {!activeField ? (
        <Box sx={styles.uploaderWrapper}>
          <MuiFileUploader
            format={['image/jpeg', 'image/png', 'image/jpg', 'image/svg']}
            onChange={handleAdd}
            withoutPreview
          />
        </Box>
      ) : (
        <>
          <Box sx={styles.contentRow}>
            <Box sx={styles.imageContainer}>
              <Box component="img" src={activeField.url || activeField.preview || ''} sx={styles.imageElement} />
              <IconButton onClick={handleDelete} size="small" sx={styles.deleteButton}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider flexItem orientation="vertical" />

            <Box sx={styles.textContainer}>
              {hasText ? (
                <Box dangerouslySetInnerHTML={{ __html: activeField.file_data?.text || '' }} sx={styles.textPreview} />
              ) : (
                <Box sx={styles.noTextState}>
                  <TextFieldsIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                  <Typography color="text.secondary" variant="body2">No text added yet</Typography>
                  <Button
                    onClick={() => setTextDialogOpen(true)}
                    size="small"
                    startIcon={<AddIcon />}
                    variant="outlined"
                  >
                    Add Text
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <Dialog fullWidth maxWidth="md" onClose={handleDialogClose} open={textDialogOpen}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Buying Guide Text
              <IconButton onClick={handleDialogClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent>
              <Controller
                control={control}
                name={`buyingGuide.${activeIndex}.file_data.text`}
                render={({ field }) => (
                  <RichTextEditorV2
                    onBlur={field.onBlur}
                    onChange={(content) => field.onChange(content)}
                    value={field.value || ''}
                  />
                )}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} variant="contained">Done</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      </AccordionDetails>
    </Accordion>
  );
};
